import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OrganizationSettingsDto } from "@fp/admin-api-client";
import { useGetConfig } from "@fp/admin-api-client/config/config.client";
import type { AxiosError } from "axios";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { AdminLayout } from "@/components/admin-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { Input } from "@fp/ui/components/base/input/input";
import { OrganizationAvatar } from "@fp/ui/components/base/avatar/organization-avatar";
import { Select, SelectItem } from "@fp/ui/components/base/select/select";
import { FileTrigger } from "@fp/ui/components/base/file-upload-trigger/file-upload-trigger";
import { ImageCropper } from "@fp/ui/components/application/image-cropper/image-cropper";
import { LeaveOrganizationModal } from "@/components/leave-organization-modal";
import { TransferOwnershipModal } from "@/components/transfer-ownership-modal";
import { Trash01, Upload01, UserRight01 } from "@untitledui/icons";
import { useUserContext } from "@/components/user-context";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import axios, { type ApiErrorResponse } from "@/lib/axios";

interface UploadUrlResult {
  uploadUrl: string;
  fileUrl: string;
  expiresAt: string;
  requiredHeaders: Record<string, string>;
}

interface BlobUrlDto {
  id: string;
  url: string;
  hash: string;
  contentType: string;
  fileSize: number;
  createdDate: string;
}

interface RegisterBlobRequest {
  url: string;
  hash: string;
  contentType: string;
  fileSize: number;
}

export const Route = createFileRoute("/organizations/edit")({
  component: RouteComponent,
});

const timeZoneLabels: Record<string, string> = {
  "America/New_York": "Eastern Time",
  "America/Chicago": "Central Time",
  "America/Denver": "Mountain Time",
  "America/Phoenix": "Arizona Time (no daylight saving)",
  "America/Los_Angeles": "Pacific Time",
  "America/Anchorage": "Alaska Time",
  "Pacific/Honolulu": "Hawaii Time",
};

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { organizations, orgId, refetchOrganizations } = useUserContext();

  const currentOrg = organizations.find((org) => org.organizationId === orgId);
  const isOwner = currentOrg?.roleCode === "ORG_OWNER";

  const [orgName, setOrgName] = useState(currentOrg?.organizationName ?? "");
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [selectedSvgFile, setSelectedSvgFile] = useState<File | null>(null);
  const [svgPreviewUrl, setSvgPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isDeleteLogoModalOpen, setIsDeleteLogoModalOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await axios.delete("/api/organizations/current/logo");
    },
    onSuccess: async () => {
      await refetchOrganizations();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: async (name: string) => {
      await axios.patch("/api/organizations/current", { name });
    },
    onSuccess: async () => {
      await refetchOrganizations();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
  });

  const { data: orgSettings } = useQuery({
    queryKey: ["organizationSettings"],
    queryFn: async () => {
      const response = await axios.get<OrganizationSettingsDto>(
        "/api/organizations/current/settings",
      );
      return response.data;
    },
  });

  const { data: configResponse } = useGetConfig();
  const timeZoneConfig = configResponse?.data.organizationTimeZone;

  const [timeZoneId, setTimeZoneId] = useState<string | null>(null);
  const selectedTimeZoneId =
    timeZoneId ?? orgSettings?.timeZoneId ?? timeZoneConfig?.defaultId ?? "";

  useEffect(() => {
    setTimeZoneId(null);
  }, [orgId]);

  const updateTimeZoneMutation = useMutation({
    mutationFn: async (newTimeZoneId: string) => {
      if (!orgSettings) return;
      await axios.put<OrganizationSettingsDto>(
        "/api/organizations/current/settings",
        { ...orgSettings, timeZoneId: newTimeZoneId },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationSettings"] });
    },
    onError: (err: AxiosError<ApiErrorResponse>) => {
      toast.error(err.response?.data?.message || "Failed to save time zone", {
        duration: Infinity,
      });
    },
  });

  const computeContentHash = async (blob: Blob): Promise<string> => {
    const buffer = await blob.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 16);
  };

  const handleLogoUpload = async (blob: Blob, contentType: string) => {
    setIsUploading(true);
    try {
      const contentHash = await computeContentHash(blob);

      // Check if blob with this hash already exists
      let blobId: string;
      try {
        const existingBlobResponse = await axios.get<BlobUrlDto>(
          `/api/blobs/by-hash/${contentHash}`,
        );
        blobId = existingBlobResponse.data.id;
      } catch {
        // Blob doesn't exist, need to upload and register
        const uploadUrlResponse = await axios.get<UploadUrlResult>(
          "/api/blobs/upload-url",
          { params: { contentType, contentHash } },
        );

        const { uploadUrl, fileUrl, requiredHeaders } = uploadUrlResponse.data;

        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: requiredHeaders,
          body: blob,
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`,
          );
        }

        // Register the blob
        const registerResponse = await axios.post<BlobUrlDto>("/api/blobs", {
          url: fileUrl,
          hash: contentHash,
          contentType,
          fileSize: blob.size,
        } as RegisterBlobRequest);

        blobId = registerResponse.data.id;
      }

      // Set the logo to reference this blob
      await axios.post("/api/organizations/current/logo", { blobId });

      await refetchOrganizations();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    } catch (error) {
      console.error("Failed to upload logo:", error);
      toast.error("Failed to upload logo. Please try again.");
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.type === "image/svg+xml") {
      setSelectedSvgFile(file);
      setSvgPreviewUrl(URL.createObjectURL(file));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFile(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleCropComplete = async (croppedBlob: Blob) => {
    await handleLogoUpload(croppedBlob, "image/png");
    setSelectedFile(null);
  };

  const handleCropCancel = () => {
    setSelectedFile(null);
  };

  const handleSvgUpload = async () => {
    if (!selectedSvgFile) return;
    await handleLogoUpload(selectedSvgFile, "image/svg+xml");
    setSelectedSvgFile(null);
    if (svgPreviewUrl) {
      URL.revokeObjectURL(svgPreviewUrl);
      setSvgPreviewUrl(null);
    }
  };

  const handleSvgCancel = () => {
    setSelectedSvgFile(null);
    if (svgPreviewUrl) {
      URL.revokeObjectURL(svgPreviewUrl);
      setSvgPreviewUrl(null);
    }
  };

  const handleDeleteLogo = () => {
    setIsDeleteLogoModalOpen(true);
  };

  const handleDeleteLogoConfirm = async () => {
    await deleteMutation.mutateAsync();
    setIsDeleteLogoModalOpen(false);
  };

  const handleSave = async () => {
    if (!orgName.trim()) {
      toast.error("Organization name is required");
      return;
    }

    if (orgName.trim() !== currentOrg?.organizationName) {
      setIsSaving(true);
      try {
        await updateNameMutation.mutateAsync(orgName.trim());
      } finally {
        setIsSaving(false);
      }
    }

    navigate({ to: "/organizations" });
  };

  if (!currentOrg) {
    return null;
  }

  // Show cropper for raster images
  if (selectedFile) {
    return (
      <ImageCropper
        imageUrl={selectedFile}
        onCropComplete={handleCropComplete}
        onCancel={handleCropCancel}
        outputSize={256}
        aspectRatio={1}
      />
    );
  }

  // Show SVG preview
  if (svgPreviewUrl) {
    return (
      <AdminLayout>
        <PageContentContainer className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Preview Logo</h3>
            <div className="flex justify-center mb-6">
              <div className="w-32 h-32 border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50">
                <img
                  src={svgPreviewUrl}
                  alt="Logo preview"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                color="secondary"
                onClick={handleSvgCancel}
                isDisabled={isUploading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleSvgUpload}
                isLoading={isUploading}
                className="flex-1"
              >
                Save Logo
              </Button>
            </div>
          </div>
        </PageContentContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Edit Organization"
        parent={{ url: "/organizations", label: "Organizations" }}
      />
      <PageContentContainer>
        <div className="max-w-lg space-y-6">
          <div>
            <h2 className="text-xl font-semibold mb-4">Organization Details</h2>
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
              <div>
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="organization-name"
                >
                  Organization Name
                </label>
                <Input
                  id="organization-name"
                  value={orgName}
                  onChange={setOrgName}
                  placeholder="Enter organization name"
                />
              </div>
              <Button
                color="primary"
                onClick={handleSave}
                isLoading={isSaving}
              >
                Save Changes
              </Button>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Time Zone</h2>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <Select
                aria-label="Time zone"
                selectedKey={selectedTimeZoneId || null}
                onSelectionChange={(key) => {
                  if (key === null) return;
                  const newTimeZoneId = String(key);
                  setTimeZoneId(newTimeZoneId);
                  updateTimeZoneMutation.mutate(newTimeZoneId);
                }}
                isDisabled={
                  updateTimeZoneMutation.isPending ||
                  !timeZoneConfig ||
                  !orgSettings
                }
              >
                {(timeZoneConfig?.supportedIds ?? []).map((id) => (
                  <SelectItem key={id} id={id}>
                    {timeZoneLabels[id] ?? id}
                  </SelectItem>
                ))}
              </Select>
              <p className="text-xs text-gray-500 mt-4">
                Applies across the organization — it sets when notifications are
                delivered and which day's completed field service work is
                discovered.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4">Logo</h2>
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-4">
                <OrganizationAvatar
                  name={currentOrg.organizationName}
                  logoUrl={currentOrg.logoUrl}
                  size="xl"
                />
                <div className="flex flex-wrap gap-2">
                  <FileTrigger
                    acceptedFileTypes={[
                      "image/png",
                      "image/jpeg",
                      "image/svg+xml",
                    ]}
                    onSelect={handleFileSelect}
                  >
                    <Button
                      color="secondary"
                      size="sm"
                      isDisabled={isUploading || deleteMutation.isPending}
                      iconLeading={Upload01}
                    >
                      {currentOrg.logoUrl ? "Change" : "Upload"}
                    </Button>
                  </FileTrigger>
                  {currentOrg.logoUrl && (
                    <Button
                      color="secondary-destructive"
                      size="sm"
                      onClick={handleDeleteLogo}
                      isLoading={deleteMutation.isPending}
                      isDisabled={isUploading || deleteMutation.isPending}
                      iconLeading={Trash01}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                PNG, JPG, or SVG. Will be cropped to a square.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-4 text-red-600">
              Danger Zone
            </h2>
            <div className="bg-white border border-red-200 rounded-lg divide-y divide-red-200">
              {isOwner && (
                <div className="flex items-center justify-between p-6">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      Transfer ownership
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Transfer ownership to another member. You will become an
                      admin.
                    </p>
                  </div>
                  <Button
                    color="secondary"
                    onClick={() => setIsTransferModalOpen(true)}
                    iconLeading={UserRight01}
                  >
                    Transfer
                  </Button>
                </div>
              )}
              <div className="flex items-center justify-between p-6">
                <div>
                  <h3 className="font-medium text-gray-900">
                    Leave this organization
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    You will lose access to all organization data.
                  </p>
                </div>
                <Button
                  color="primary-destructive"
                  onClick={() => setIsLeaveModalOpen(true)}
                >
                  Leave
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PageContentContainer>

      <LeaveOrganizationModal
        isOpen={isLeaveModalOpen}
        onClose={() => setIsLeaveModalOpen(false)}
        organizationName={currentOrg.organizationName}
        isOwner={isOwner}
        onTransferRequested={() => setIsTransferModalOpen(true)}
      />

      <TransferOwnershipModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        organizationName={currentOrg.organizationName}
      />

      <ConfirmationModal
        isOpen={isDeleteLogoModalOpen}
        onClose={() => setIsDeleteLogoModalOpen(false)}
        onConfirm={handleDeleteLogoConfirm}
        title="Remove Logo"
        message="Are you sure you want to remove the organization logo?"
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
      />
    </AdminLayout>
  );
}
